import { createContext, useContext, useEffect } from 'react';

/**
 * Dynamic page-title override.
 *
 * Route handles are static, so a page showing a named entity ("Acme Corp")
 * pushes its title through this context instead. Split in two (value and
 * setter) so pages that only ever set a title never re-render when it changes.
 *
 * Both defaults are real no-ops: pages are routinely rendered in tests without
 * a provider, and must keep working there.
 */
export const PageTitleOverrideContext = createContext<string | null>(null);
export const SetPageTitleOverrideContext = createContext<(title: string | null) => void>(() => {});

/** Current dynamic override, or null when the route handle should be used. */
export function usePageTitleOverride(): string | null {
  return useContext(PageTitleOverrideContext);
}

/**
 * Publish a dynamic title for the current route, typically an entity name.
 *
 * Pass `null`/`undefined` to fall back to the route handle, which is what makes
 * this a single call above a component's early returns rather than one per
 * loading / not-found / loaded branch:
 *
 *   useSetPageTitle(company ? company.name : failed ? 'Company not found' : null);
 *
 * The override is cleared on unmount, so navigating away restores the handle.
 */
export function useSetPageTitle(title: string | null | undefined): void {
  const setOverride = useContext(SetPageTitleOverrideContext);

  useEffect(() => {
    setOverride(title ?? null);
    return () => setOverride(null);
  }, [setOverride, title]);
}
